import { useState } from 'react';
import { useSelector } from 'react-redux';
import { getDb } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { parseFieldMarkup } from '../../utils/TextProcessing';
import { parseFirestorePath } from '../../utils/Schema';
import { useParams } from 'react-router-dom';

/*
    Hook to return the correct database connection for various parts of the application
*/
const useDatabase = () => {
  const { types } = useSelector(state => state.Content);
  const { parentRoutes } = useSelector(state => state.Routing);
  const [rootParentDocument, setRootParentDocument] = useState();
  const user = useSelector(state => state.User);
  const params = useParams();

  const getDbConnection = async ({
    relationship,
    directParentDocument,
    databaseId,
    contentTypeName
  }) => {
    const relatedType = types.find(
      t => t.type === relationship?.contentTypeName
    );

    const type = types.find(t => t.type === contentTypeName);

    let rel = relationship;
    let parentType = type
      ? types?.find(t =>
          t.relationships?.find(
            r => r.contentTypeName === type?.type && r.type === 'child'
          )
        )
      : undefined;

    let firestoreDatabaseId = '(default)';

    if (type && !type?.hasOwnCollection && !rel) {
      // We have no relationship passed in. This will be because we have a type that we have chosen to show in the sidebar
      // but it is actually a child type and we need to find the parent relationship

      rel = parentType.relationships.find(
        r => r.contentTypeName === type.type && r.type === 'child'
      );

      if (rel.useUserOrganisationId) {
        const userOrg = user.organisations.find(
          o => o.organisationId === user.selectedOrganisationId
        );
        firestoreDatabaseId = userOrg.databaseId;
      }
    } else if (
      rel?.contentTypeName !== 'rel' &&
      databaseId?.trim().length > 0
    ) {
      firestoreDatabaseId = databaseId;
    } else if (rel?.databaseId?.trim().length > 0) {
      firestoreDatabaseId = rel.databaseId;

      if (rel.databaseId.indexOf('{') > -1) {
        if (directParentDocument) {
          firestoreDatabaseId = parseFieldMarkup({
            record: directParentDocument,
            fieldMarkup: firestoreDatabaseId,
            returnAsString: true
          });
        }

        if (rootParentDocument) {
          firestoreDatabaseId = parseFieldMarkup({
            record: rootParentDocument,
            fieldMarkup: firestoreDatabaseId,
            returnAsString: true
          });
        }

        if (firestoreDatabaseId.indexOf('{') > -1 && !rootParentDocument) {
          const db = getDb();

          const rootParentRoute = parentRoutes.find(
            p => p.collection === true && !p.rel
          );

          const rootParentDocumentId = params[rootParentRoute?.idField];

          let parentDoc = {};
          if (
            rootParentDocumentId &&
            rootParentDocumentId === directParentDocument?.id
          ) {
            parentDoc = directParentDocument;
            setRootParentDocument(directParentDocument);
          } else {
            // rootparentroute is undefined
            const parentPath = parseFirestorePath({
              route: rootParentRoute,
              params
            });

            const parentSnap = await getDoc(
              doc(db, parentPath, rootParentDocumentId)
            );
            parentDoc = parentSnap.data();
            setRootParentDocument(parentDoc);
          }

          firestoreDatabaseId = parseFieldMarkup({
            record: parentDoc,
            fieldMarkup: rel.databaseId,
            returnAsString: true
          });

          if (firestoreDatabaseId.indexOf('{') > -1) {
            if (relatedType?.databaseId?.trim().length > 0) {
              firestoreDatabaseId = relatedType.databaseId;
            } else {
              firestoreDatabaseId = '(default)';
            }
          }
        }
      } else if (rel.databaseId.indexOf('/') > -1) {
        // a database path has been provided to get a document that contains a field called databaseId
        const segments = rel.databaseId.split('/');
        Object.keys(params).forEach(param => {
          const index = segments.findIndex(s => s === `:${param}`);
          if (index > -1) {
            segments[index] = params[param];
          }
        });

        const sourceDb = getDb(
          segments[0] === '(default)' || segments[0] === 'default'
            ? undefined
            : segments[0]
        );
        const sourceId = segments[segments.length - 1];
        segments.splice(0, 1);
        segments.splice(segments.length - 1, 1);

        const docRef = doc(sourceDb, segments.join('/'), sourceId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const sourceDoc = docSnap.data();
          if (sourceDoc.databaseId?.trim().length > 0) {
            firestoreDatabaseId = sourceDoc.databaseId;
          }
        }
      }
    } else if (relatedType?.databaseId?.trim().length > 0) {
      firestoreDatabaseId = relatedType?.databaseId;
    } else if (type?.databaseId?.trim().length > 0) {
      firestoreDatabaseId = type.databaseId;
    }

    return {
      db: getDb(firestoreDatabaseId),
      databaseId: firestoreDatabaseId,
      rootParentDocument,
      parentType
    };
  };

  return { getDb: getDbConnection };
};

export default useDatabase;
